import UserAuthService from "./UserAuthService";
import { createApiError, throwFetchError } from "../errors/api";
import { ApiError, AuthenticationError, FrontendError } from "../errors";

export type Options = {
    headers?: HeadersInit;
};

function isHandledError(error: unknown): error is FrontendError | ApiError {
    return error instanceof FrontendError || error instanceof ApiError;
}

export function rethrowHandledError(error: unknown): never {
    if (isHandledError(error)) {
        throw error;
    }
    throwFetchError(error);
}

/**
 * BaseApiService - Provides common functionality for API calls
 * - Authentication headers
 * - Centralized error handling
 * - Standard HTTP methods
 * - Response parsing
 */
class BaseApiService {
    private readonly authService: UserAuthService;
    private readonly baseUrl: string;

    constructor(authService: UserAuthService, apiBaseUrl: string) {
        this.authService = authService;
        this.baseUrl = apiBaseUrl + "/api";
    }

    /**
     * Get authenticated headers for API requests
     */
    private getHeaders(additionalHeaders?: HeadersInit, includeContentType: boolean = true): HeadersInit {
        const user = this.authService.getUser();
        if (!user) {
            throw new AuthenticationError("User not authenticated");
        }

        return {
            Authorization: `Bearer ${user.token}`,
            ...(includeContentType && { "Content-Type": "application/json" }),
            ...additionalHeaders,
        };
    }

    /**
     * Handle API response with centralized error handling
     */
    private async handleResponse(response: Response, context: string = "API call"): Promise<unknown> {
        // Check for authentication errors first
        if (response.status === 401) {
            // if error is 401, the frontend might need to reauthenticate / logout (mheicke)
            const errorResult = await this.authService.handleApiError(response.clone(), context);
            if (errorResult.handled) {
                throw new AuthenticationError("Authentication failed", { context: errorResult.error });
            }
        }

        // Handle HTTP errors with structured error objects
        if (!response.ok) {
            throw await createApiError(response);
        }

        // Parse successful response
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
            return await response.json();
        } else {
            return await response.text();
        }
    }

    /**
     * Generic GET request
     */
    async get(endpoint: string) {
        try {
            const url = `${this.baseUrl}${endpoint}`;
            const response = await fetch(url, {
                method: "GET",
                headers: this.getHeaders(),
            });

            return await this.handleResponse(response, `GET ${endpoint}`);
        } catch (error) {
            rethrowHandledError(error);
        }
    }

    /**
     * GET request that returns a Blob payload
     */
    async getBlob(endpoint: string, additionalHeaders: HeadersInit | undefined = undefined): Promise<Blob> {
        try {
            const url = `${this.baseUrl}${endpoint}`;

            const fetchOptions = {
                method: "GET",
                headers: this.getHeaders(additionalHeaders, false),
            };

            const response = await fetch(url, fetchOptions);

            if (!response.ok) {
                // only needed here - otherwise is already integrated into handleResponse function (mheicke)
                throw await createApiError(response);
            }

            return await response.blob();
        } catch (error) {
            rethrowHandledError(error);
        }
    }

    /**
     * Generic POST request
     */
    async post(endpoint: string, data: {} | undefined = undefined) {
        try {
            const url = `${this.baseUrl}${endpoint}`;
            const response = await fetch(url, {
                method: "POST",
                headers: this.getHeaders(),
                body: data ? JSON.stringify(data) : null,
            });

            return this.handleResponse(response, `POST ${endpoint}`);
        } catch (error) {
            rethrowHandledError(error);
        }
    }

    /**
     * Generic DELETE request
     */
    async delete(endpoint: string) {
        try {
            const url = `${this.baseUrl}${endpoint}`;
            const response = await fetch(url, {
                method: "DELETE",
                headers: this.getHeaders(),
            });

            return this.handleResponse(response, `DELETE ${endpoint}`);
        } catch (error) {
            rethrowHandledError(error);
        }
    }

    /**
     * Generic PUT request
     */
    async put(endpoint: string, data: {} | undefined = undefined) {
        try {
            const url = `${this.baseUrl}${endpoint}`;
            const response = await fetch(url, {
                method: "PUT",
                headers: this.getHeaders(),
                body: data ? JSON.stringify(data) : null,
            });

            return this.handleResponse(response, `PUT ${endpoint}`);
        } catch (error) {
            rethrowHandledError(error);
        }
    }

    /**
     * Upload file (multipart/form-data)
     */
    async upload(endpoint: string, formData: FormData, options: Options = {}) {
        try {
            const url = `${this.baseUrl}${endpoint}`;

            // Don't set Content-Type for FormData - let browser set it with boundary
            const headers = this.getHeaders(options.headers, false);
            const response = await fetch(url, {
                method: "POST",
                headers: headers,
                body: formData,
                ...options,
            });

            return this.handleResponse(response, `UPLOAD ${endpoint}`);
        } catch (error) {
            rethrowHandledError(error);
        }
    }
}

export default BaseApiService;
